import { inject, Injectable } from '@angular/core';
import { PluginInjectorApplicationPlugin, PluginInjectorService, IWidgetPermissions } from 'core-client/api';

// Thruster Module
import { ThrusterAddComponent } from './thruster/thruster-add/thruster-add.component';
import { ThrusterViewComponent } from './thruster/thruster-view/thruster-view.component';
import {
  ThrusterSettingsComponent
} from './thruster/thruster-settings/thruster-settings.component';

// Compass Module
import { CompassAddComponent } from './compass/compass-add/compass-add.component';
import { CompassViewComponent } from './compass/compass-view/compass-view.component';
import { CompassSettingsComponent } from './compass/compass-settings/compass-settings.component';

// instrument-field
import { InstrumentFieldAddComponent } from './instrument-field/instrument-field-add/instrument-field-add.component';
import { InstrumentFieldViewComponent } from './instrument-field/instrument-field-view/instrument-field-view.component';
import { InstrumentFieldSettingsComponent } from './instrument-field/instrument-field-settings/instrument-field-settings.component';

@Injectable({
  providedIn: 'root'
})
export class OiclOpenbridgeService {
  private readonly pluginInjector: PluginInjectorService = inject(PluginInjectorService);

  private readonly plugin: PluginInjectorApplicationPlugin.IApplicationPlugin = <PluginInjectorApplicationPlugin.IApplicationPlugin>{}
  private readonly pluginName: string = 'OICL_OPENBRIDGE';
  private widgetPermissions: IWidgetPermissions = {
    thruster: {
      oicl_openbridge_thruster_enabled: false,
      oicl_openbridge_thruster_widget_header_display: false,
      oicl_openbridge_thruster_settings_edit: false,
      oicl_openbridge_thruster_ngsild_connector_enabled: false
    },
    compass: {
      oicl_openbridge_compass_enabled: false,
      oicl_openbridge_compass_widget_header_display: false,
      oicl_openbridge_compass_settings_edit: false,
      oicl_openbridge_compass_ngsild_connector_enabled: false
    },
    instrument_field: {
      oicl_openbridge_instrument_field_enabled: false,
      oicl_openbridge_instrument_field_widget_header_display: false,
      oicl_openbridge_instrument_field_settings_edit: false,
      oicl_openbridge_instrument_field_ngsild_connector_enabled: false
    }
  };

  private readonly THRUSTER_MODULE = {
    pluginModule: 'THRUSTER',
    pluginId: '',
    widgetPermissions: {
      widgetEnabledPermission: {
        permissionRef: 'oicl_openbridge_thruster_enabled'
      },
      widgetBasePermissions: {
        widgetSettingsEnabledPermission: {
          permissionRef: 'oicl_openbridge_thruster_settings_edit'
        },
        autorizeDisplayOfWidgetHeader: {
          permissionRef: 'oicl_openbridge_thruster_widget_header_display'
        }
      },
      widgetAdditionalPermissions: {
        authorizeNgsildConnector: {
          permissionRef: 'oicl_openbridge_thruster_ngsild_connector_enabled'
        }
      }
    },
    addComponent: {
      title: '#TRANSLATE-LATER#OICL_OPENBRIDGE.THRUSTER.WIDGET.TITLE',
      componentName: 'ThrusterAddComponent',
      component: ThrusterAddComponent,
    },
    viewComponent: {
      title: 'Thruster',
      componentName: 'ThrusterViewComponent',
      component: ThrusterViewComponent,
    },
    settingComponent: {
      componentName: 'ThrusterSettingsComponent',
      component: ThrusterSettingsComponent,
      dialogConfig: {
        width: this.pluginInjector.getMenuDialogConfig().width
      }
    }
  };

  private readonly COMPASS_MODULE = {
    pluginModule: 'COMPASS',
    pluginId: '',
    widgetPermissions: {
      widgetEnabledPermission: {
        permissionRef: 'oicl_openbridge_compass_enabled'
      },
      widgetBasePermissions: {
        widgetSettingsEnabledPermission: {
          permissionRef: 'oicl_openbridge_compass_settings_edit'
        },
        autorizeDisplayOfWidgetHeader: {
          permissionRef: 'oicl_openbridge_compass_widget_header_display'
        }
      },
      widgetAdditionalPermissions: {
        authorizeNgsildConnector: {
          permissionRef: 'oicl_openbridge_compass_ngsild_connector_enabled'
        }
      }
    },
    addComponent: {
      title: '#TRANSLATE-LATER#OICL_OPENBRIDGE.COMPASS.WIDGET.TITLE',
      componentName: 'CompassAddComponent',
      component: CompassAddComponent,
    },
    viewComponent: {
      title: 'Compass',
      componentName: 'CompassViewComponent',
      component: CompassViewComponent,
    },
    settingComponent: {
      componentName: 'CompassSettingsComponent',
      component: CompassSettingsComponent,
      dialogConfig: {
        width: this.pluginInjector.getMenuDialogConfig().width
      }
    }
  };

  private readonly INSTRUMENT_FIELD_MODULE = {
    pluginModule: 'INSTRUMENT_FIELD',
    pluginId: '',
    widgetPermissions: {
      widgetEnabledPermission: {
        permissionRef: 'oicl_openbridge_instrument_field_enabled'
      },
      widgetBasePermissions: {
        widgetSettingsEnabledPermission: {
          permissionRef: 'oicl_openbridge_instrument_field_settings_edit'
        },
        autorizeDisplayOfWidgetHeader: {
          permissionRef: 'oicl_openbridge_instrument_field_widget_header_display'
        }
      },
      widgetAdditionalPermissions: {
        authorizeNgsildConnector: {
          permissionRef: 'oicl_openbridge_instrument_field_ngsild_connector_enabled'
        }
      }
    },
    addComponent: {
      title: '#TRANSLATE-LATER#OICL_OPENBRIDGE.INSTRUMENT_FIELD.WIDGET.TITLE',
      componentName: 'InstrumentFieldAddComponent',
      component: InstrumentFieldAddComponent,
    },
    viewComponent: {
      title: 'Instrument Field',
      componentName: 'InstrumentFieldViewComponent',
      component: InstrumentFieldViewComponent,
    },
    settingComponent: {
      componentName: 'InstrumentFieldSettingsComponent',
      component: InstrumentFieldSettingsComponent,
      dialogConfig: {
        width: this.pluginInjector.getMenuDialogConfig().width
      }
    }
  };

  readonly pluginModule: PluginInjectorApplicationPlugin.IPluginModule = {
    widgets: [
      this.THRUSTER_MODULE,
      this.COMPASS_MODULE,
      this.INSTRUMENT_FIELD_MODULE
    ]
  };

  async setup(): Promise<void> {
    this.initPlugin();
  }

  private initPlugin(): void {
    this.plugin.name = this.pluginName.toUpperCase();
    this.plugin.enabled = false;
    this.plugin.description = `OICL OpenBridge Plugin - Note: Thruster Widget is currently not Firefox compatible.`;
    this.plugin.permissions = {
      widget_permissions: this.widgetPermissions
    };
    this.plugin.modules = [this.pluginModule];
    this.plugin.version = '0.0.1';

    this.pluginInjector.register(this.plugin);
  }
}
